import { createContext, useContext, useState, useEffect, useCallback } from "react";
import translations from "./translations";

const I18nContext = createContext(null);

export const LANGUAGES = [
  { code: "pt", label: "PT", name: "Português" },
  { code: "en", label: "EN", name: "English" },
  { code: "es", label: "ES", name: "Español" },
];

const STORAGE_KEY = "locadora.lang";

function detectInitial() {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && translations[stored]) return stored;
  const nav = (navigator.language || "en").toLowerCase();
  if (nav.startsWith("pt")) return "pt";
  if (nav.startsWith("es")) return "es";
  return "en";
}

function getNested(obj, path) {
  return path.split(".").reduce((acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined), obj);
}

function interpolate(str, vars) {
  if (typeof str !== "string" || !vars) return str;
  return str.replace(/\{\{(\w+)\}\}/g, (_, k) => (vars[k] !== undefined ? vars[k] : `{{${k}}}`));
}

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() => detectInitial());

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, lang);
    document.documentElement.setAttribute("lang", lang);
  }, [lang]);

  const setLang = useCallback((code) => {
    if (translations[code]) setLangState(code);
  }, []);

  const t = useCallback((key, vars) => {
    const value = getNested(translations[lang], key)
      ?? getNested(translations.en, key)
      ?? key;
    return interpolate(value, vars);
  }, [lang]);

  // Currency / number formatter helpers based on language
  const locale = lang === "pt" ? "pt-BR" : lang === "es" ? "es-US" : "en-US";
  const fmtCurrency = useCallback((v) =>
    new Intl.NumberFormat(locale, { style: "currency", currency: "USD" }).format(v || 0),
  [locale]);
  const fmtNumber = useCallback((v) => new Intl.NumberFormat(locale).format(v || 0), [locale]);

  return (
    <I18nContext.Provider value={{ lang, setLang, t, locale, fmtCurrency, fmtNumber }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useT() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useT must be used inside I18nProvider");
  return ctx;
}
